import { select } from 'redux-saga/effects';
import {
  PendingQueueItemKey,
  PendingQueueItemKeyWithoutSequence,
} from '../../../../core/database/pendingQueue/pendingQueueStorage';
import { RootState } from '../../../../core/redux/store';
import {
  SheetCommand,
  SheetCommandType,
} from '../../../types/commands/sheetCommands/sheetCommands';
import { SheetGroupsCommandType } from '../../../types/commands/sheetGroupsCommands/sheetGroupCommands';
import {
  PartialSheetManagementSet,
  SheetManagementSet,
  RawCommandsHistory,
  CommandHistory,
} from '../../../types/sheet/draft';
import { v4 as uuid } from 'uuid';
import sessionStorageSingleton from '../../../../core/configs/sessionStorageSingleton';
import { finishDraft } from 'immer';
import { AppWindow } from '../../../../windows/types';
import { findWindowBySheetId } from '../../../../windows/redux/windowsSlice';
import { CellPosition, GridEntity, Sheet, SheetView } from '../../../types';
import { getViewport, getViewportCols } from '../../../utils/views/viewport';
import { Direction } from '../../../../windows/helpers/adjustWindow';

interface GeneratePendingQueueKey {
  keyWithoutSequence: PendingQueueItemKeyWithoutSequence;
  sheetsCommands: RawCommandsHistory;
  sheetFeKeys: Map<string, CryptoKey | undefined>;
}
export const generatePendingQueueKey = async (
  userId: string,
  present: Partial<CommandHistory>
): Promise<GeneratePendingQueueKey> => {
  const sheetIds: Set<string> = new Set();
  const groupIds: Set<string> = new Set();

  const sheetFeKeys: Map<string, CryptoKey | undefined> = new Map();

  // console.log('set === 2', present);

  const sheetsCommands: RawCommandsHistory = {
    creationCommands: present.creationCommands,
    deletionCommands: present.deletionCommands,
    sheetChanges: present.sheetChanges,
    entityIdToNameMapChanges: present.entityIdToNameMapChanges,
  };

  await getSheetAndGroupIdsFromCRUDEvents(
    sheetIds,
    groupIds,
    present.creationCommands,
    present.deletionCommands,
    sheetFeKeys
  );

  if (present.groupCommands) {
    if (!sheetsCommands.groupCommands) sheetsCommands.groupCommands = {};
    // for (let key of Object.keys(present.groupCommands ?? {})) {
    for (let key of present.groupCommands?.keys()) {
      sheetsCommands.groupCommands[key] = present.groupCommands?.get(key)!;
      groupIds.add(key);
    }
  }

  if (present.sheetCommands) {
    if (!sheetsCommands.sheetCommands) sheetsCommands.sheetCommands = {};

    // for (let sheetId of Object.keys(present.sheetCommands ?? {})) {
    for (let sheetId of present.sheetCommands?.keys()) {
      const feKey = await sessionStorageSingleton.getFeKey(sheetId);
      sheetFeKeys.set(sheetId, feKey);

      sheetsCommands.sheetCommands[sheetId] =
        present.sheetCommands?.get(sheetId)!;

      sheetIds.add(sheetId);
    }
  }

  // for (let sheetId of Object.keys(present.sheetChanges ?? {})) {
  if (present.sheetChanges)
    for (let sheetId of present.sheetChanges?.keys()) {
      const feKey = await sessionStorageSingleton.getFeKey(sheetId);
      sheetFeKeys.set(sheetId, feKey);

      sheetIds.add(sheetId);
    }

  return {
    keyWithoutSequence: [userId, Array.from(sheetIds), Array.from(groupIds)],
    sheetsCommands,
    sheetFeKeys,
  };
};

const getSheetAndGroupIdsFromCRUDEvents = async (
  sheetIds: Set<string>,
  groupIds: Set<string>,
  creationCommands: RawCommandsHistory['creationCommands'],
  deletionCommands: RawCommandsHistory['deletionCommands'],
  sheetFeKeys: Map<string, CryptoKey | undefined>
) => {
  for (const command of creationCommands ?? []) {
    if (command.type === SheetCommandType.CreateSheet) {
      const sheetId = command.sheet.id;
      sheetIds.add(sheetId);

      const feKey = await sessionStorageSingleton.getFeKey(sheetId);
      sheetFeKeys.set(sheetId, feKey);
    } else if (command.type === SheetGroupsCommandType.CreateGroup) {
      groupIds.add(command.group.id);
    }
  }

  for (const command of deletionCommands ?? []) {
    if (command.type === SheetCommandType.DeleteSheet) {
      sheetIds.add(command.sheetId);
    } else if (command.type === SheetGroupsCommandType.DeleteGroup) {
      groupIds.add(command.groupId);
    }
  }
};

export function* generateSheetsDataSet() {
  const userId: string = yield select((state: RootState) => state.auth.user.id);
  const {
    staticSheets,
    staticSheetGroups,
    staticSheetViews,
    unSavedChanges,
    primaryStorage,
    entityIdToNameMap,
  } = yield select((state: RootState) => ({
    staticSheetGroups: state.sheetsManager.sheetGroups,
    staticSheets: state.sheetsManager.sheets,
    staticSheetViews: state.sheetsManager.sheetViews,
    unSavedChanges: state.sheetsManager.unSavedChanges,
    primaryStorage: state.auth.app.primaryStorage,
    entityIdToNameMap: state.sheetsManager.entityIdToNameMap,
  }));

  const set: SheetManagementSet = {
    id: uuid(),
    userId,

    present: {},
    past: {},

    // sheetChanges: new Map(),

    updatedGroups: new Map(),

    deletedGroups: new Set(),
    deletedSheets: new Set(),

    temporarySheets: new Map(),
    updatedSheets: new Map(),
    updatedSheetViews: new Map(),

    draftedSheets: new Map(),

    staticSheets,
    staticSheetViews,
    staticSheetGroups,
    storage: primaryStorage,
    // updatedEntityIdToNameMap: undefined,
    staticEntityIdToNameMap: entityIdToNameMap,
    // entityIdToNameMapChanges: [],
  };

  return set;
}

export const updateAndStoreDraftedSheets = (set: SheetManagementSet) => {
  set.draftedSheets.forEach((draft, id) => {
    const updatedSheet = finishDraft(draft, (changes) => {
      if (!set.present.sheetChanges) set.present.sheetChanges = new Map();

      set.present.sheetChanges.set(id, changes);
    });
    // console.log('updatedSheet', updatedSheet, set.present.sheetChanges);
    set.updatedSheets.set(id, {
      ...updatedSheet,
      ...set.updatedSheets.get(id),
    });
  });
};

export function* adjustSheetViewports(set: SheetManagementSet) {
  let keys = set.updatedSheets.keys();

  while (true) {
    let result = keys.next();
    if (result.done) break;
    const sheetId = result.value;
    const partialSheet = set.updatedSheets.get(sheetId);
    const window: AppWindow | undefined = yield select(
      findWindowBySheetId(sheetId)
    );
    const sheet = set.staticSheets[sheetId];
    const sheetView = set.staticSheetViews[sheetId];
    const commands = set.present.sheetCommands?.get(sheetId);
    if (!window || !sheet || !sheetView || !commands) continue;

    const partialSheetView = set.updatedSheetViews.get(sheetId) ?? {};
    const updatedSheet = {
      ...sheet,
      ...partialSheet,
    };
    const updatedSheetView = {
      ...sheetView,
      ...partialSheetView,
    };

    // TODO: Use this command to get the target cell position e.g if it cell entry area for styles etc then set it accordingly
    const firstCommand = commands[0];
    const cellPosition = getCellPositionFromCommand(firstCommand);

    const allViewport = getViewport(
      window.width,
      window.height,
      updatedSheet,
      updatedSheetView,
      Direction.Right,
      Direction.Bottom,
      {
        cellPosition,
        // fitToCenter: true,
      }
    );

    set.updatedSheetViews.set(sheetId, {
      ...partialSheetView,
      ...allViewport,
    });
  }
}

const getCellPositionFromCommand = (command: SheetCommand) => {
  const cellPosition: Partial<CellPosition> = {};
  if (command.type === SheetCommandType.FreezeGrid) {
    const alias = command.entity === GridEntity.Column ? 'ci' : 'ri';
    cellPosition[alias] = command.range.end + 1;
  }

  return cellPosition;
};
